import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpDown, CloudUpload, FileSpreadsheet, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { formatDate, formatMoney, formatPeriod } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sortBy, setSortBy] = useState("uploaded_at");
  const [sortDir, setSortDir] = useState("desc");
  const inputRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/reports");
      setReports(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const form = new FormData();
        form.append("file", file);
        const r = await api.post("/reports", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(
          `Report ${r.data.po_number} (${formatPeriod(r.data.period_month, r.data.period_label)}) erfolgreich erfasst.`
        );
      } catch (err) {
        toast.error(`${file.name}: ${apiErrorMessage(err)}`);
      }
    }
    setUploading(false);
    reload();
  };

  const onInputChange = (e) => {
    handleFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(Array.from(e.dataTransfer.files || []));
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/reports/${deleteTarget.id}`);
      toast.success("Report gelöscht");
      setDeleteTarget(null);
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      // Sensible defaults: dates and numbers descending first, strings ascending
      setSortDir(["po_number", "type"].includes(column) ? "asc" : "desc");
    }
  };

  const sortedReports = useMemo(() => {
    const arr = [...reports];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv), "de", { numeric: true }) * dir;
    });
    return arr;
  }, [reports, sortBy, sortDir]);

  return (
    <div data-testid="reports-page">
      <PageHeader
        title="Reports"
        subtitle="Provisionsberichte"
        actions={
          <Button
            onClick={() => inputRef.current?.click()}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
            data-testid="reports-upload-btn"
          >
            <CloudUpload size={16} className="mr-2" /> Report hochladen
          </Button>
        }
      >
        Alle hochgeladenen Lightspeed-Provisionsberichte (Payment- und Lizenz-Provision).
      </PageHeader>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8 space-y-6">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed rounded-sm px-6 py-10 text-center transition-colors ${
            dragging
              ? "border-slate-900 bg-slate-50"
              : "border-slate-200 bg-white hover:bg-slate-50"
          }`}
          data-testid="reports-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            multiple
            className="hidden"
            onChange={onInputChange}
            data-testid="reports-file-input"
          />
          <div className="mx-auto w-12 h-12 bg-slate-900 text-white flex items-center justify-center rounded-sm mb-3">
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <CloudUpload size={22} strokeWidth={1.4} />
            )}
          </div>
          <div className="font-display text-lg font-bold text-slate-900 tracking-tight">
            Excel-Reports per Drag &amp; Drop hochladen
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Mehrere Dateien gleichzeitig möglich. Akzeptiert .xlsx von Lightspeed (Payment- und
            Lizenz-Provision).
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                <SortHeader column="po_number" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="left">PO-Nummer</SortHeader>
                <SortHeader column="type" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="left">Typ</SortHeader>
                <SortHeader column="period_month" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="left">Periode</SortHeader>
                <SortHeader column="total_commission" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right">Provision</SortHeader>
                <SortHeader column="entry_count" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right">Einträge</SortHeader>
                <SortHeader column="customer_count" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right">Kunden</SortHeader>
                <SortHeader column="new_customer_count" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right">Neue</SortHeader>
                <SortHeader column="missing_customer_count" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right">Fehlend</SortHeader>
                <SortHeader column="discrepancy_count" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right">Abweich.</SortHeader>
                <SortHeader column="uploaded_at" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="left">Hochgeladen</SortHeader>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-slate-500">
                    Lade Reports …
                  </td>
                </tr>
              )}
              {!loading && reports.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center">
                    <FileSpreadsheet size={28} className="mx-auto text-slate-300 mb-2" />
                    <div className="text-slate-500">Noch keine Reports hochgeladen.</div>
                  </td>
                </tr>
              )}
              {sortedReports.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-slate-100 hover:bg-slate-50"
                  data-testid={`report-row-${r.po_number}`}
                >
                  <td className="px-4 py-3 font-mono font-semibold">
                    <Link
                      to={`/reports/${r.id}`}
                      className="hover:underline underline-offset-2"
                      data-testid={`report-link-${r.po_number}`}
                    >
                      {r.po_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={r.type} />
                  </td>
                  <td className="px-4 py-3">{formatPeriod(r.period_month, r.period_label)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">
                    {formatMoney(r.total_commission)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{r.entry_count}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.customer_count}</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {r.new_customer_count > 0 ? (
                      <span className="text-blue-700 font-semibold">+{r.new_customer_count}</span>
                    ) : (
                      "0"
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {r.missing_customer_count > 0 ? (
                      <span className="text-amber-700 font-semibold">{r.missing_customer_count}</span>
                    ) : (
                      "0"
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {r.discrepancy_count > 0 ? (
                      <span className="text-rose-700 font-semibold">{r.discrepancy_count}</span>
                    ) : (
                      "0"
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{formatDate(r.uploaded_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setDeleteTarget(r)}
                      className="text-slate-400 hover:text-rose-600 transition-colors"
                      data-testid={`report-delete-${r.po_number}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="report-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Report wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.po_number} ({formatPeriod(deleteTarget?.period_month, deleteTarget?.period_label)}) wird unwiderruflich gelöscht. Alle zugehörigen Einträge und Soll/Ist-Daten gehen verloren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-report"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SortHeader({ column, sortBy, sortDir, onSort, align = "left", children }) {
  const active = sortBy === column;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  const justify = align === "right" ? "justify-end" : "justify-start";
  return (
    <th className={`text-${align} px-4 py-3 font-semibold select-none`}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1 ${justify} w-full hover:text-slate-900 transition-colors ${
          active ? "text-slate-900" : ""
        }`}
        data-testid={`sort-${column}`}
      >
        <span>{children}</span>
        <Icon size={12} className={active ? "text-slate-900" : "text-slate-400"} />
      </button>
    </th>
  );
}
